/**
 * Splits a `RecipePreviewStep.text` into plain/quantity/ingredient segments.
 *
 * The backend embeds model-authored inline tags — `<t k="quantity">1-2 EL</t>`,
 * `<t k="ingredient">Ahornsirup</t>` — into otherwise-plain instruction text. This is
 * NOT trusted HTML (it's LLM output), so it must never be bound via `[innerHTML]`.
 * This function only ever recognizes the exact `<t k="quantity|ingredient">…</t>`
 * pattern via regex; everything else — including any other tag-like substring the
 * model might produce — is treated as literal plain text. Callers must render the
 * resulting segments through ordinary (auto-escaping) Angular template interpolation,
 * never as HTML, so a stray "<script>" in the text renders as inert visible text
 * instead of being interpreted.
 */
export type RecipeTextSegmentKind = 'text' | 'quantity' | 'ingredient';

export interface RecipeTextSegment {
  kind: RecipeTextSegmentKind;
  text: string;
}

const TAG_PATTERN = /<t k="(quantity|ingredient)">([\s\S]*?)<\/t>/g;

export function parseRecipeStepText(text: string): RecipeTextSegment[] {
  const segments: RecipeTextSegment[] = [];
  let lastIndex = 0;

  TAG_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TAG_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({kind: 'text', text: text.slice(lastIndex, match.index)});
    }
    segments.push({kind: match[1] as 'quantity' | 'ingredient', text: match[2]});
    lastIndex = TAG_PATTERN.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({kind: 'text', text: text.slice(lastIndex)});
  }

  return segments;
}
